function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useCallback, useContext, useMemo, useState } from 'react';
import DebugBoundaryVisibleContext from '../internal/provider/DebugBoundaryVisibleContext';
import { ALL_CATEGORIES } from '../public/useDebugBoundaryVisible';
const border = 2;
const vtPad = 1;
const hzPad = 3;
const outerStyles = {
    background: 'transparent',
    border: 'solid 2px transparent',
    boxSizing: 'border-box',
    display: 'block',
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: '300'
};
const innerStyles = {
    boxSizing: 'border-box',
    color: '#fff',
    display: 'block',
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '10px',
    fontStyle: 'none',
    fontWeight: 'bold',
    maxWidth: '100%',
    overflow: 'hidden',
    padding: `${vtPad}px ${hzPad + border}px ${vtPad + border}px ${hzPad}px`,
    position: 'absolute',
    textDecoration: 'none',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};
function addOpacity(color) {
    switch(color.length){
        case 4:
            return `${color}9`;
        case 7:
            return `${color}99`;
        default:
            throw new Error(`Invalid color passed: ${color}`);
    }
}
const initialSizeStyles = {
    left: 0,
    top: 0,
    width: 0,
    height: 0
};
const pollMap = new Map();
let activeInterval;
let activeCount = 0;
function updateCount(add) {
    activeCount += add;
    if (activeCount >= 0 && !activeInterval) {
        activeInterval = setInterval(pollPositions, 100);
    }
    if (activeCount <= 0 && activeInterval) {
        activeInterval = clearInterval(activeInterval);
    }
}
function pollPositions() {
    pollMap.forEach((node, setSizeStyles)=>{
        const el = node.nextElementSibling;
        if (el instanceof HTMLElement) {
            setSizeStyles({
                left: el.offsetLeft,
                top: el.offsetTop,
                width: el.offsetWidth,
                height: el.offsetHeight
            });
        } else {
            setSizeStyles({
                left: 0,
                top: 0,
                width: 0,
                height: 0
            });
        }
    });
}
export default /*#__PURE__*/ memo(function DebugBoundary({ category, children, color, name, visible }) {
    const [visibleCategories] = useContext(DebugBoundaryVisibleContext);
    const [sizeStyles, setSizeStyles] = useState(initialSizeStyles);
    const shouldShow = React.useMemo(()=>{
        if (visible) {
            return true;
        }
        if (visibleCategories.size === 0) {
            return false;
        }
        if (visibleCategories.has(ALL_CATEGORIES)) {
            return true;
        }
        if (category && visibleCategories.has(category)) {
            return true;
        }
        return false;
    }, [
        visible,
        visibleCategories,
        category
    ]);
    const fullVisible = shouldShow && category && name;
    const outerStyle = useMemo(()=>_object_spread_props(_object_spread({}, outerStyles, sizeStyles), {
            borderColor: addOpacity(color)
        }), [
        sizeStyles,
        color
    ]);
    const innerStyle = useMemo(()=>_object_spread_props(_object_spread({}, innerStyles), {
            backgroundColor: addOpacity(color)
        }), [
        color
    ]);
    const updateSize = useMemo(()=>{
        let oldValue = initialSizeStyles;
        return (newValue)=>{
            if (newValue.left !== oldValue.left || newValue.top !== oldValue.top || newValue.width !== oldValue.width || newValue.height !== oldValue.height) {
                // eslint-disable-next-line react-hooks/immutability
                oldValue = newValue;
                setSizeStyles(newValue);
            }
        };
    }, []);
    const nodeRef = useCallback((node)=>{
        if (node) {
            pollMap.set(updateSize, node);
            updateCount(+1);
        } else {
            pollMap.delete(updateSize);
            updateCount(-1);
        }
    }, [
        updateSize
    ]);
    const node = name && category && fullVisible ? /*#__PURE__*/ _jsx("div", {
        ref: nodeRef,
        "data-testid": "debug-boundary",
        style: outerStyle,
        children: /*#__PURE__*/ _jsx("div", {
            style: innerStyle,
            children: `${name} (${category})`
        })
    }) : null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            node,
            children
        ]
    });
});

//# sourceMappingURL=DebugBoundary.js.map